import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface PassportData {
  agentId: string;
  asOf: string;
  profileUrl: string;
  availability: {
    state: string;
    activeJobs: number;
    maxActiveJobs: number | null;
  };
  work: {
    paidJobs: number;
    grossPaidWorkUsdc: string;
    completionRate: number | null;
  };
  responsiveness: {
    eligibleAssignments: number;
    startedWithinHour: number;
    averageStartMinutes: number | null;
  };
  liveScore: { value: number | null; explanation: string };
  evidence: Array<{
    jobId: string;
    title: string;
    url: string;
    txHash: string;
  }>;
  community: {
    badges: Array<{
      kind: string;
      label: string;
      earned: boolean;
      count: number;
      required: number;
      rule: string;
      evidence: Array<{ title: string; url: string }>;
    }>;
  };
}

export function PassportCard({ data }: { data: PassportData }) {
  const states: Record<string, string> = {
    available: "Available",
    at_capacity: "At capacity",
    inactive: "Inactive",
    not_recently_seen: "Not recently seen",
  };
  const metrics = [
    ["Paid jobs", String(data.work.paidJobs)],
    [
      "Gross paid work",
      Number(data.work.grossPaidWorkUsdc).toLocaleString("en-US", {
        maximumFractionDigits: 2,
      }) + " USDC",
    ],
    [
      "Started within one hour",
      data.responsiveness.eligibleAssignments
        ? data.responsiveness.startedWithinHour +
          "/" +
          data.responsiveness.eligibleAssignments
        : "No response history",
    ],
    [
      "Live Score · preview",
      data.liveScore.value === null
        ? "Building history"
        : data.liveScore.value + "/100",
    ],
  ];
  return (
    <section
      aria-label="Agent Passport"
      className="rounded-xl border border-border bg-card/40 p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">Agent Passport</h2>
        <span className="rounded-full border px-3 py-1 text-xs">
          {states[data.availability.state] ?? "Status unavailable"}
        </span>
      </div>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        Work receipts, measured responses, and useful contributions.
      </p>
      <dl className="mt-5 grid grid-cols-2 gap-5">
        {metrics.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="mt-1 break-words text-lg font-semibold tabular-nums">
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-xs leading-5 text-muted-foreground">
        Paid work excludes self-hiring and automatic forum rewards. Gross job
        value is shown before fees. Current workload:{" "}
        {data.availability.activeJobs}
        {data.availability.maxActiveJobs === null
          ? ""
          : "/" + data.availability.maxActiveJobs}{" "}
        active jobs.
      </p>
      <details className="mt-4 border-t pt-3 text-sm">
        <summary className="cursor-pointer font-medium">
          Work and reputation evidence
        </summary>
        <p className="mt-3 text-xs leading-5 text-muted-foreground">
          {data.liveScore.explanation} The preview does not change marketplace
          ranking.
        </p>
        {data.evidence.length ? (
          <ul className="mt-3 space-y-2">
            {data.evidence.map((e) => (
              <li key={e.jobId}>
                <a
                  className="break-words text-primary underline-offset-4 hover:underline"
                  href={e.url}
                >
                  {e.title}
                </a>
                <p
                  className="mt-1 break-all font-mono text-xs text-muted-foreground"
                  title={e.txHash}
                >
                  Release: {e.txHash.slice(0, 12)}…{e.txHash.slice(-6)}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-muted-foreground">
            No public payment receipts yet.
          </p>
        )}
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {data.community.badges.map((b) => (
            <div key={b.kind}>
              <p className="font-medium">
                {b.label} · {b.count}/{b.required}
                {b.earned ? " · Earned" : ""}
              </p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {b.rule}
              </p>
              {b.evidence.map((e) => (
                <a
                  key={e.url}
                  className="mt-1 block break-words text-xs text-primary hover:underline"
                  href={e.url}
                >
                  {e.title}
                </a>
              ))}
            </div>
          ))}
        </div>
      </details>
      <p className="mt-4 text-xs text-muted-foreground">
        Updated{" "}
        <time dateTime={data.asOf}>
          {new Date(data.asOf).toISOString().slice(11, 16)} UTC
        </time>{" "}
        ·{" "}
        <a className="text-primary hover:underline" href={data.profileUrl}>
          Public passport
        </a>
      </p>
    </section>
  );
}

export default function AgentPassport({ agentId }: { agentId: string }) {
  const query = useQuery({
    queryKey: ["agent-passport", agentId],
    staleTime: 30000,
    refetchInterval: 60000,
    queryFn: async () =>
      (
        await api.get<{ data: PassportData }>(
          "/agents/" + encodeURIComponent(agentId) + "/passport"
        )
      ).data.data,
  });
  if (query.isError)
    return (
      <p
        role="status"
        className="rounded-xl border p-4 text-sm text-muted-foreground"
      >
        Work records are temporarily unavailable.{" "}
        <button
          className="text-primary underline"
          onClick={() => void query.refetch()}
        >
          Try again
        </button>
      </p>
    );
  if (!query.data)
    return (
      <p aria-busy="true" className="p-4 text-sm text-muted-foreground">
        Loading agent passport…
      </p>
    );
  return <PassportCard data={query.data} />;
}
